import React, { useContext } from 'react';
import { Dialog, DialogTitle, DialogContent, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { PdfViewer } from '../../../Utils/PdfViewer/PdfViewer';
import { I18nReactContext } from '../../../contexts/I18nContext';
import './FilePreview.scss';

/**
 * File preview content component that renders different file types
 * @param {Object} props - Component props
 * @param {Object} props.file - File object containing signedUrl, mimeType, and fileName
 * @param {Function} [props.onReadingCompleted] - Optional callback for PDF reading completion
 * @returns {JSX.Element} File preview content
 */
const FilePreviewContent = ({ file, onReadingCompleted = () => {} }) => {
    const { i18n } = useContext(I18nReactContext);

    if (!file || !file.signedUrl) {
        return (
            <div className="filePreview__unsupported">
                <p>{i18n.translate('filePreview.noPreviewAvailable')}</p>
            </div>
        );
    }

    switch (file.mimeType) {
        case 'application/pdf':
            return (
                <div className="filePreview__pdf">
                    <PdfViewer url={file.signedUrl} onReadingCompleted={onReadingCompleted} />
                </div>
            );

        case 'image/jpeg':
        case 'image/jpg':
        case 'image/png':
            return (
                <div className="filePreview__image">
                    <img
                        src={file.signedUrl}
                        alt={file.fileName}
                        className="filePreview__imageElement"
                    />
                </div>
            );

        default:
            return (
                <div className="filePreview__unsupported">
                    <p>{i18n.translate('filePreview.notAvailable')}</p>
                    <p className="filePreview__downloadHint">
                        <a href={file.signedUrl} target="_blank" rel="noopener noreferrer">
                            {i18n.translate('filePreview.downloadToView')}
                        </a>
                    </p>
                </div>
            );
    }
};

/**
 * File preview component with Dialog wrapper
 * @param {Object} props - Component props
 * @param {Object} props.file - File object containing signedUrl, mimeType, and fileName
 * @param {boolean} props.open - Controls whether the dialog is open
 * @param {Function} props.onClose - Callback when the dialog should close
 * @param {Function} [props.onReadingCompleted] - Optional callback for PDF reading completion
 * @returns {JSX.Element} File preview dialog component
 */
export const FilePreview = ({ file, open, onClose, onReadingCompleted = () => {} }) => {
    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle className="previewDialogTitle">
                {file?.fileName || ''}
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <FilePreviewContent file={file} onReadingCompleted={onReadingCompleted} />
            </DialogContent>
        </Dialog>
    );
};

/**
 * Check if a file can be previewed based on its MIME type
 * @param {string} mimeType - MIME type of the file
 * @returns {boolean} Whether the file can be previewed
 */
export const isPreviewableFile = (mimeType) => {
    const previewableTypes = [
        'application/pdf',
        'image/jpeg',
        'image/jpg',
        'image/png'
    ];
    return previewableTypes.includes(mimeType);
};
